import { supabase } from '../lib/supabase';

export interface FeatureVisibilitySetting {
  id: string;
  feature_key: string;
  is_enabled: boolean;
  feature_name: string;
  feature_group: 'core' | 'management' | 'admin' | 'help';
  description: string | null;
  created_at: string;
  updated_at: string;
}

// Cache for feature settings
let cachedSettings: Map<string, boolean> | null = null;
let cacheTimestamp: number = 0;
const CACHE_TTL = 60000; // 1 minute cache

export class FeatureVisibilityService {
  /**
   * Get all feature visibility settings
   */
  static async getAllFeatureSettings(): Promise<FeatureVisibilitySetting[]> {
    try {
      const { data, error } = await supabase
        .from('feature_visibility_settings')
        .select('*')
        .order('feature_group', { ascending: true })
        .order('feature_name', { ascending: true });

      if (error) {
        console.error('Error fetching feature visibility settings:', error);
        // Schema drift or empty table — fail-open (show app; all features on)
        return [];
      }

      // Update cache
      if (data) {
        cachedSettings = new Map();
        data.forEach(setting => {
          cachedSettings!.set(setting.feature_key, setting.is_enabled);
        });
        cacheTimestamp = Date.now();
      }

      return data || [];
    } catch (error) {
      console.error('Error in getAllFeatureSettings:', error);
      return [];
    }
  }

  /**
   * Check if a specific feature is enabled
   */
  static async getFeatureEnabled(featureKey: string): Promise<boolean> {
    // Check cache first
    if (cachedSettings && Date.now() - cacheTimestamp < CACHE_TTL) {
      const cached = cachedSettings.get(featureKey);
      if (cached !== undefined) {
        return cached;
      }
    }

    try {
      const { data, error } = await supabase
        .from('feature_visibility_settings')
        .select('is_enabled')
        .eq('feature_key', featureKey)
        .maybeSingle();

      if (error) {
        console.error('Error fetching feature visibility:', error);
        // Default to enabled if there's an error (fail-open approach)
        return true;
      }

      const isEnabled = data?.is_enabled ?? true;

      // Update cache
      if (!cachedSettings) {
        cachedSettings = new Map();
      }
      cachedSettings.set(featureKey, isEnabled);
      cacheTimestamp = Date.now();

      return isEnabled;
    } catch (error) {
      console.error('Error in getFeatureEnabled:', error);
      // Default to enabled if there's an error (fail-open approach)
      return true;
    }
  }

  /**
   * Get list of enabled feature keys
   */
  static async getEnabledFeatures(): Promise<string[]> {
    try {
      const settings = await this.getAllFeatureSettings();
      return settings
        .filter(setting => setting.is_enabled)
        .map(setting => setting.feature_key);
    } catch (error) {
      console.error('Error in getEnabledFeatures:', error);
      // Return empty array on error, which will show all features as disabled
      return [];
    }
  }

  /**
   * Update feature visibility
   */
  static async updateFeatureVisibility(
    featureKey: string,
    isEnabled: boolean
  ): Promise<void> {
    try {
      const { error } = await supabase
        .from('feature_visibility_settings')
        .update({ 
          is_enabled: isEnabled,
          updated_at: new Date().toISOString()
        })
        .eq('feature_key', featureKey);

      if (error) {
        console.error('Error updating feature visibility:', error);
        throw error;
      }

      // Update cache
      if (cachedSettings) {
        cachedSettings.set(featureKey, isEnabled);
        cacheTimestamp = Date.now();
      }
    } catch (error) {
      console.error('Error in updateFeatureVisibility:', error);
      throw error;
    }
  }

  /**
   * Clear cache (useful after updates)
   */
  static clearCache(): void {
    cachedSettings = null;
    cacheTimestamp = 0;
  }

  /**
   * Get cached feature enabled status (synchronous, may return undefined if not cached)
   */
  static getCachedFeatureEnabled(featureKey: string): boolean | undefined {
    if (cachedSettings && Date.now() - cacheTimestamp < CACHE_TTL) {
      return cachedSettings.get(featureKey);
    }
    return undefined;
  }
}

